import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import ClientLayout from "./layouts/ClientLayout";
import LoginLayout from "./layouts/LoginLayout";
import AdminLayout from "./layouts/AdminLayout";
import CorporateLayout from "./layouts/CorporateLayout";
import RequestorLayout from "./layouts/RequestorLayout";

import TrackAndTrace from "./pages/client/TrackAndTrace";
import ShipmentManagement from "./pages/admin/ShipmentManagement";
import TransactionHistory from "./pages/admin/TransactionHistory";
import Dashboard from "./pages/admin/Dashboard";
import CreateShipment from "./pages/admin/CreateShipment";
import BillingUpload from "./pages/admin/BillingUpload";
import BillingList from "./pages/admin/BillingList";
import DocumentTemplates from "./pages/admin/DocumentTemplates";
import EmployeeJoLedger from "./pages/admin/JoLedger";
import AdminJoRequests from "./pages/admin/JoRequests";
import SuperCreateShipment from "./pages/superAdmin/CreateShipment";

import CorporateShipmentManagement from "./pages/corporate/ShipmentManagement";
import CorporateDashboard from "./pages/corporate/Dashboard";
import CorporateAnalytics from "./pages/corporate/Analytics";
import RequestorDashboard from "./pages/requestor/Dashboard";
import RequestorRequestCreate from "./pages/requestor/RequestCreate";
import RequestorRequestSummary from "./pages/requestor/RequestSummary";
import RequestorLiquidationReport from "./pages/requestor/LiquidationReport";
import RequestorJoLedger from "./pages/requestor/JoLedger";

import SuperAdminLayout from "./layouts/SuperAdminLayout";
import SuperDashboard from "./pages/superAdmin/SuperDashboard";
import CreateUser from "./pages/superAdmin/CreateUser";
import Analytics from "./pages/superAdmin/Analytics";

import DepartmentManagement from "./pages/superAdmin/DepartmentManagement";
import NotFound from "./pages/NotFound";
import ClientFAQ from "./pages/client/ClientFAQ";
import Tracking from "./pages/client/Tracking/Tracking";
import Login from "./pages/LoginSystem/Login";
import Signup from "./pages/SignupSystem/Signup";
import "./assets/css/css-loaders.css";
import { useDispatch, useSelector } from "react-redux";
import CheckAuth from "./components/CheckAuth";
import UnauthPage from "./pages/UnauthPage";
import { checkAuth } from "./store/auth-slice";
import EmployeeDirectory from "./pages/superAdmin/EmployeeDirectory";
import SuperShipmentManagement from "./pages/superAdmin/SuperShipmentManagement";
import RequestorApproval from "./pages/superAdmin/RequestorApproval";
import SuperAdminJoLedger from "./pages/superAdmin/JoLedger";
import SuperAdminJoRequests from "./pages/superAdmin/JoRequests";
import AuditLogs from "./pages/superAdmin/AuditLogs";
import AccountingLayout from "./layouts/AccountingLayout";
import AccountingDashboard from "./pages/accounting/AccountingDashboard";
import VendorEnlistment from "./pages/accounting/VendorEnlistment";
import CustomerEnlistment from "./pages/accounting/CustomerEnlistment";
import Particulars from "./pages/accounting/Particulars";
import RequestCreate from "./pages/accounting/RequestCreate";
import RequestSummary from "./pages/accounting/RequestSummary";
import CheckVoucherCreate from "./pages/accounting/CheckVoucherCreate";
import CheckVoucherList from "./pages/accounting/CheckVoucherList";
import PendingVoucherCheck from "./pages/accounting/PendingVoucherCheck";
import Profile from "./pages/Profile";
import ChartOfAccounts from "./pages/accounting/ChartOfAccounts";
import LiquidationWorkflow from "./pages/accounting/LiquidationWorkflow";
import LiquidationTeamLeaderApprovals from "./pages/accounting/LiquidationTeamLeaderApprovals";
import LiquidationUser127Approvals from "./pages/accounting/LiquidationUser127Approvals";
import CostAnalysisWorkflow from "./pages/accounting/CostAnalysisWorkflow";
import CostAnalysisTemplates from "./pages/accounting/CostAnalysisTemplates";
import CostAnalysisInitialApprovals from "./pages/accounting/CostAnalysisInitialApprovals";
import CostAnalysisUser127Approvals from "./pages/accounting/CostAnalysisUser127Approvals";
import ProductionReport from "./pages/accounting/ProductionReport";
import JoLedger from "./pages/accounting/JoLedger";
import JoRequests from "./pages/accounting/JoRequests";

function App() {
  const { user, isAuthenticated, isLoading } = useSelector(
    (state) => state.auth
  );

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);

  if (isLoading) {
    return (
      <div className="flex w-full h-screen justify-center items-center bg-blue font-poppins relative overflow-hidden">
        {/* Subtle background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-20 w-64 h-64 bg-yellow/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-white/3 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative z-10 flex flex-col items-center gap-8">
          {/* Main loader container */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10 flex flex-col items-center gap-6">
            {/* Truck with properly sized spinner */}
            <div className="relative">
              <i className="fa-solid fa-truck-fast text-5xl text-yellow animate-smooth-drive"></i>
              {/* Larger spinner to contain the truck */}
              <div className="absolute inset-0 -m-8">
                <div className="w-20 h-20 border-3 border-white/20 border-t-white rounded-full animate-spin"></div>
              </div>
            </div>
            
            {/* Loading text */}
            <div className="text-center">
              <h2 className="text-white font-semibold text-xl mb-2">
                Loading Application
              </h2>
              <p className="text-white/70 text-sm">
                Preparing your dashboard...
              </p>
            </div>

            {/* Simple progress dots */}
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-yellow rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-yellow/70 rounded-full animate-bounce delay-100"></div>
              <div className="w-2 h-2 bg-yellow/50 rounded-full animate-bounce delay-200"></div>
            </div>
          </div>

          {/* Simple route indicator */}
          <div className="flex items-center gap-4">
            <div className="w-3 h-3 bg-white/30 rounded-full animate-pulse"></div>
            <div className="w-12 h-0.5 bg-white/30"></div>
            <div className="w-2 h-2 bg-yellow rounded-full animate-ping"></div>
            <div className="w-12 h-0.5 bg-white/30"></div>
            <div className="w-3 h-3 bg-white/30 rounded-full animate-pulse delay-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/auth"
          element={
            <CheckAuth
              isAuthenticated={isAuthenticated}
              user={user}
              isLoading={isLoading}
            >
              <LoginLayout />
            </CheckAuth>
          }
        >
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Signup />} />
        </Route>

        {/* Client-side routes */}
        <Route path="/" element={<ClientLayout />}>
          <Route index element={<TrackAndTrace />} /> {/* Default route */}
          <Route path="/client/ClientFAQ" element={<ClientFAQ />} />
          <Route path="tracking/:trackingNumber" element={<Tracking />} />
          <Route path="*" element={<NotFound />} />{" "}
          {/* Catch-all route for 404 */}
        </Route>

        {/* Admin-side routes */}
        <Route
          path="/admin"
          element={
            <CheckAuth
              isAuthenticated={isAuthenticated}
              user={user}
              isLoading={isLoading}
            >
              <AdminLayout />
            </CheckAuth>
          }
        >
          <Route path="shipment-management" element={<ShipmentManagement />} />
          <Route path="transaction-history" element={<TransactionHistory />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="create-shipment" element={<CreateShipment />} />
          <Route path="request-create" element={<RequestCreate />} />
          <Route path="request-summary" element={<RequestSummary />} />
          <Route
            path="liquidation"
            element={
              <LiquidationWorkflow
                pageDescription="Review RFPs you created that are marked for liquidation and open the modal to upload documents or complete the workflow."
                scope="created"
              />
            }
          />
          <Route
            path="liquidation-approvals"
            element={<LiquidationTeamLeaderApprovals />}
          />
          <Route
            path="liquidation-user127-approvals"
            element={<LiquidationUser127Approvals />}
          />
          <Route path="cost-analysis" element={<CostAnalysisWorkflow />} />
          <Route path="cost-analysis-templates" element={<CostAnalysisTemplates />} />
          <Route
            path="cost-analysis-approvals"
            element={<CostAnalysisInitialApprovals />}
          />
          <Route
            path="cost-analysis-initial-approvals"
            element={<CostAnalysisInitialApprovals />}
          />
          <Route
            path="cost-analysis-user127-approvals"
            element={<CostAnalysisUser127Approvals />}
          />
          <Route path="billing-upload" element={<BillingUpload />} />
          <Route path="billing-list" element={<BillingList />} />
          <Route path="document-templates" element={<DocumentTemplates />} />
          <Route
            path="billed-shipments"
            element={<ShipmentManagement billedOnly />}
          />
          <Route path="jo-ledger" element={<EmployeeJoLedger />} />
          <Route path="jo-requests" element={<AdminJoRequests />} />
          <Route path="jo-ledger/unliquidated" element={<EmployeeJoLedger />} />
          <Route path="jo-ledger/:jobOrder" element={<EmployeeJoLedger />} />
          <Route path="jo-ledger/unliquidated/:jobOrder" element={<EmployeeJoLedger />} />
          <Route path="profile" element={<Profile />} />
        </Route>

        {/* Corporate-side routes */}
        <Route
          path="/corporate"
          element={
            <CheckAuth
              isAuthenticated={isAuthenticated}
              user={user}
              isLoading={isLoading}
            >
              <CorporateLayout />
            </CheckAuth>
          }
        >
          <Route path="corporate-shipment-management" element={<CorporateShipmentManagement />} />
          {/* <Route path="transaction-history" element={<TransactionHistory />} /> */}
          <Route path="corporate-dashboard" element={<CorporateDashboard />} />
          <Route path="corporate-analytics" element={<CorporateAnalytics />} />
          {/* <Route path="create-shipment" element={<CreateShipment />} /> */}
        </Route>

        {/* Requestor-side routes */}
        <Route
          path="/requestor"
          element={
            <CheckAuth
              isAuthenticated={isAuthenticated}
              user={user}
              isLoading={isLoading}
            >
              <RequestorLayout />
            </CheckAuth>
          }
        >
          <Route index element={<RequestorDashboard />} />
          <Route path="dashboard" element={<RequestorDashboard />} />
          <Route path="profile" element={<Profile />} />
          <Route path="request-create" element={<RequestorRequestCreate />} />
          <Route path="request-summary" element={<RequestorRequestSummary />} />
          <Route path="liquidation-report" element={<RequestorLiquidationReport />} />
          <Route path="jo-ledger" element={<RequestorJoLedger />} />
          <Route path="jo-ledger/unliquidated" element={<RequestorJoLedger />} />
          <Route path="jo-ledger/:jobOrder" element={<RequestorJoLedger />} />
          <Route path="jo-ledger/unliquidated/:jobOrder" element={<RequestorJoLedger />} />
        </Route>

        {/* Super-Admin-side routes */}
        <Route
          path="/superAdmin"
          element={
            <CheckAuth
              isAuthenticated={isAuthenticated}
              user={user}
              isLoading={isLoading}
            >
              <SuperAdminLayout />
            </CheckAuth>
          }
        >
          <Route path="super-dashboard" element={<SuperDashboard />} />
          <Route path="create-user" element={<CreateUser />} />
          <Route path="create-shipment" element={<SuperCreateShipment />} />
          <Route path="department-management" element={<DepartmentManagement />} />
          <Route
            path="shipment-management"
            element={<SuperShipmentManagement />}
          />
          <Route path="employee-directory" element={<EmployeeDirectory />} />
          <Route path="audit-logs" element={<AuditLogs />} />
          <Route path="requestor-approval" element={<RequestorApproval />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="billing-upload" element={<BillingUpload />} />
          <Route path="billing-list" element={<BillingList />} />
          <Route path="document-templates" element={<DocumentTemplates />} />
          <Route
            path="billed-shipments"
            element={<SuperShipmentManagement billedOnly />}
          />
          <Route
            path="liquidation-approvals"
            element={<LiquidationTeamLeaderApprovals />}
          />
          <Route
            path="liquidation-user127-approvals"
            element={<LiquidationUser127Approvals />}
          />
          <Route path="cost-analysis" element={<CostAnalysisWorkflow />} />
          <Route path="cost-analysis-templates" element={<CostAnalysisTemplates />} />
          <Route
            path="cost-analysis-approvals"
            element={<CostAnalysisInitialApprovals />}
          />
          <Route
            path="cost-analysis-initial-approvals"
            element={<CostAnalysisInitialApprovals />}
          />
          <Route
            path="cost-analysis-user127-approvals"
            element={<CostAnalysisUser127Approvals />}
          />
          <Route path="jo-ledger" element={<SuperAdminJoLedger />} />
          <Route path="jo-requests" element={<SuperAdminJoRequests />} />
          <Route path="jo-ledger/unliquidated" element={<SuperAdminJoLedger />} />
          <Route path="jo-ledger/:jobOrder" element={<SuperAdminJoLedger />} />
          <Route path="jo-ledger/unliquidated/:jobOrder" element={<SuperAdminJoLedger />} />
          <Route path="profile" element={<Profile />} />
        </Route>

        {/* Accounting routes (Superadmin or Accounting department) */}
        <Route
          path="/accounting"
          element={
            <CheckAuth
              isAuthenticated={isAuthenticated}
              user={user}
              isLoading={isLoading}
            >
              <AccountingLayout />
            </CheckAuth>
          }
        >
          <Route index element={<AccountingDashboard />} />
          <Route path="vendors" element={<VendorEnlistment />} />
          <Route path="customers" element={<CustomerEnlistment />} />
          <Route path="particulars" element={<Particulars />} />
          <Route path="chart-accounts" element={<ChartOfAccounts />} />
          <Route path="requests/create" element={<RequestCreate />} />
          <Route path="requests" element={<RequestSummary />} />
          <Route path="liquidation" element={<LiquidationWorkflow />} />
          <Route
            path="liquidation-approvals"
            element={<LiquidationTeamLeaderApprovals />}
          />
          <Route
            path="liquidation-user127-approvals"
            element={<LiquidationUser127Approvals />}
          />
          <Route path="cost-analysis" element={<CostAnalysisWorkflow />} />
          <Route path="cost-analysis-templates" element={<CostAnalysisTemplates />} />
          <Route
            path="cost-analysis-approvals"
            element={<CostAnalysisInitialApprovals />}
          />
          <Route
            path="cost-analysis-initial-approvals"
            element={<CostAnalysisInitialApprovals />}
          />
          <Route
            path="cost-analysis-user127-approvals"
            element={<CostAnalysisUser127Approvals />}
          />
          <Route path="check-vouchers" element={<CheckVoucherList />} />
          <Route path="check-voucher/create" element={<CheckVoucherCreate />} />
          <Route path="check-vouchers/pending" element={<PendingVoucherCheck />} />
          <Route path="production-report" element={<ProductionReport />} />
          <Route path="jo-ledger" element={<JoLedger />} />
          <Route path="jo-requests" element={<JoRequests />} />
          <Route path="jo-ledger/unliquidated" element={<JoLedger />} />
          <Route path="jo-ledger/unliquidated/:jobOrder" element={<JoLedger />} />
          <Route path="jo-ledger/:jobOrder" element={<JoLedger />} />
          <Route path="profile" element={<Profile />} />
        </Route>

        {/* Login-side routes */}
        {/* <Route path="/LoginSystem" element={<LoginLayout />}>
          <Route path="/LoginSystem/Login" element={<Login />} />
        </Route> */}

        {/* Signup-side routes */}
        {/* <Route path="/SignupSystem" element={<SignupLayout />}>
          <Route path="/SignupSystem/Signup" element={<Signup />} />
        </Route> */}

        {/* Separate Login route if needed */}
        <Route path="/login" element={<LoginLayout />}>
          <Route index element={<Login />} /> {/* Default login route */}
        </Route>


        <Route path="/unauth-page" element={<UnauthPage />} />
      </Routes>
    </Router>
  );
}

export default App;
